import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface Crumb {
  label: string
  href?: string
}

interface HeaderProps {
  /**
   * Breadcrumb trail for the current page (computed from the route).
   */
  breadcrumbs?: Crumb[]
  /**
   * Page-level actions to display on the right of the header
   */
  actions?: React.ReactNode
  /**
   * Mobile menu toggle callback
   */
  onMenuToggle?: () => void
  /**
   * Whether to show mobile menu button
   */
  showMobileMenu?: boolean
  /**
   * Additional class names
   */
  className?: string
}

/**
 * Header component following PRD layout structure:
 * - 60px fixed height
 * - Logo (left)
 * - Breadcrumb/Title (center-left)
 * - Notifications | User Avatar+Dropdown (right)
 *
 * @example
 * <Header
 *   title={<Breadcrumb items={[{ label: 'Projects' }, { label: 'My Project' }]} />}
 *   actions={
 *     <>
 *       <NotificationButton />
 *       <UserMenu />
 *     </>
 *   }
 * />
 */
export function Header({
  breadcrumbs,
  actions,
  onMenuToggle,
  showMobileMenu = false,
  className,
}: HeaderProps) {
  return (
    <header
      className={cn(
        'h-[60px] border-b bg-card flex items-center justify-between px-4 lg:px-6',
        className
      )}
    >
      {/* Left Section: Mobile Menu + Title */}
      <div className="flex items-center gap-4">
        {/* Mobile Menu Toggle */}
        {showMobileMenu && (
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onMenuToggle}
            aria-label="Toggle menu"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
              role="presentation"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </Button>
        )}

        {/* Breadcrumb trail */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5 text-sm">
            {breadcrumbs.map((c, i) => (
              <div key={`${c.label}-${i}`} className="flex min-w-0 items-center gap-1.5">
                {i > 0 && <span className="text-muted-foreground/50">/</span>}
                {c.href ? (
                  <Link
                    to={c.href as string}
                    className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {c.label}
                  </Link>
                ) : (
                  <span className="truncate font-medium text-foreground">{c.label}</span>
                )}
              </div>
            ))}
          </nav>
        )}
      </div>

      {/* Right Section: page actions */}
      <div className="flex items-center gap-1">
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    </header>
  )
}

/**
 * Simple breadcrumb component
 */
interface BreadcrumbItem {
  label: string
  href?: string
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
}

export function Breadcrumb({ items }: BreadcrumbProps) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm">
      {items.map((item, index) => (
        <div key={item.label} className="flex items-center gap-2">
          {index > 0 && <span className="text-muted-foreground">/</span>}
          {item.href ? (
            <a
              href={item.href}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {item.label}
            </a>
          ) : (
            <span className={index === items.length - 1 ? 'font-medium' : 'text-muted-foreground'}>
              {item.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  )
}
